import type { NavItem, NavSubItem, NavSubMenu } from "@/data/navData";
import { canAny, canAnyIn } from "@/lib/permissions";
import type { MyPermissions } from "@/types/authorization";
import type { OrgSettings } from "@/types/organization/settings";

export interface NavVisibilityContext {
  capabilities: MyPermissions | undefined | null;
  /** Org feature flag lookup — bought, not permitted. */
  isEnabled: (featureKey: string) => boolean;
  settings: OrgSettings | null | undefined;
}

function isNavSubMenu(item: NavSubItem | NavSubMenu): item is NavSubMenu {
  return "items" in item;
}

/**
 * Whether the entry's own gates pass — feature flag, org setting, capability.
 *
 * Three independent questions in a fixed order: does the org have the feature,
 * has it switched the behaviour on, and may this person do it. An entry with no
 * gates is baseline and always passes.
 */
function passesOwnGates(
  entry: { featureKey?: string; settingKey?: keyof OrgSettings } & {
    permission?: string | string[];
    module?: string;
    anyModule?: string[];
  },
  ctx: NavVisibilityContext
): boolean {
  if (entry.featureKey && !ctx.isEnabled(entry.featureKey)) return false;
  if (entry.settingKey && !ctx.settings?.[entry.settingKey]) return false;
  if (entry.module && !canAnyIn(ctx.capabilities, entry.module)) return false;
  if (
    entry.anyModule &&
    !entry.anyModule.some((m) => canAnyIn(ctx.capabilities, m))
  )
    return false;
  if (entry.permission) {
    const codes = Array.isArray(entry.permission)
      ? entry.permission
      : [entry.permission];
    if (!canAny(ctx.capabilities, codes)) return false;
  }
  return true;
}

function filterChildren(
  items: (NavSubItem | NavSubMenu)[],
  ctx: NavVisibilityContext
): (NavSubItem | NavSubMenu)[] {
  const kept: (NavSubItem | NavSubMenu)[] = [];
  for (const item of items) {
    if (!passesOwnGates(item, ctx)) continue;
    if (isNavSubMenu(item)) {
      const children = filterChildren(item.items, ctx);
      // A group whose every child is hidden is an empty group. Rendering it
      // would offer a heading that opens onto nothing.
      if (children.length === 0) continue;
      kept.push({ ...item, items: children });
    } else {
      kept.push(item);
    }
  }
  return kept;
}

/** The first reachable leaf url under a pruned section, if any. */
export function firstLeafUrl(item: NavItem): string | undefined {
  const walk = (entries: (NavSubItem | NavSubMenu)[]): string | undefined => {
    for (const entry of entries) {
      if (isNavSubMenu(entry)) {
        const url = walk(entry.items);
        if (url) return url;
      } else if (entry.url && entry.url !== "#") {
        return entry.url;
      }
    }
    return undefined;
  };
  return walk(item.items ?? []);
}

/**
 * Prune a nav tree to what this user may actually reach.
 *
 * **Presentation only.** The server re-derives authority on every request; this
 * exists so nobody is shown a door that will not open. A section survives only
 * if its own gates pass *and* something is left underneath it — or, when it
 * sets `emptyUrl`, as a plain link to that page.
 */
export function visibleNavItems(
  items: NavItem[],
  ctx: NavVisibilityContext
): NavItem[] {
  const kept: NavItem[] = [];
  for (const item of items) {
    if (!passesOwnGates(item, ctx)) continue;
    if (item.items && item.items.length > 0) {
      const children = filterChildren(item.items, ctx);
      if (children.length === 0) {
        if (item.emptyUrl) kept.push({ ...item, url: item.emptyUrl, items: [] });
        continue;
      }
      kept.push({ ...item, items: children });
    } else {
      kept.push(item);
    }
  }
  return kept;
}
